/**
 * /livret-circulation/print — Version imprimable / PDF du Livret Circulation
 *
 * Cette page déploie l'intégralité des 7 chapitres du livret circulation
 * en un seul document long, optimisé pour l'impression et l'export PDF via
 * la fonctionnalité native "Imprimer → Enregistrer en PDF" du navigateur.
 *
 * Design d'impression :
 *   - Fond blanc, texte noir (économie d'encre, meilleure lisibilité)
 *   - Sauts de page entre chapitres (page-break-before: always)
 *   - Cases à cocher vides (impression = livret vierge à remplir)
 *   - Marges A4 confortables (@page CSS)
 *   - Auto-déclenchement de window.print() au chargement
 *
 * Usage :
 *   → depuis le livret : bouton "Télécharger PDF" ouvre cette page
 *   → cette page lance window.print()
 *   → l'utilisateur choisit "Enregistrer au format PDF" (natif iOS/Android/Desktop)
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Printer, ArrowLeft, Bike, CheckSquare, Square } from 'lucide-react';

const CHAPTERS_DATA = [
  {
    n: '01',
    title: 'Bienvenue en circulation',
    subtitle: 'Du plateau à la route réelle',
    intro: "Sur le plateau, tu maîtrises ta moto seule sur un terrain fermé. En circulation, tu partages la route avec voitures, camions, piétons, cyclistes — chacun avec ses propres priorités, imprévus et angles morts. L'épreuve circulation dure ~40 min, coefficient 3 à la note finale.",
    items: [
      { label: 'REGARD LOIN', desc: 'Anticipe 12s à venir, pas ton pneu' },
      { label: 'DOUCEUR', desc: 'Accélération et freinage progressifs' },
      { label: 'PLACEMENT', desc: '1/3 droit hors agglo · milieu en ville' },
      { label: 'RÉGULARITÉ', desc: 'Une vitesse adaptée, sans à-coup' },
    ],
  },
  {
    n: '02',
    title: 'Prise en main avant départ',
    subtitle: 'EPI complet + vérifications techniques',
    intro: "Depuis novembre 2016 : casque homologué E22-06 + gants CE moto obligatoires. Le reste (blouson, pantalon, bottes, gilet) est fortement recommandé — un examinateur le vérifiera visuellement.",
    section1Title: '🦺 Équipement de protection individuelle',
    items: [
      { label: 'Casque homologué E22-06', desc: 'Attaché correctement, jugulaire tendue' },
      { label: 'Gants CE homologués moto', desc: 'Étiquette avec picto moto obligatoire' },
      { label: 'Blouson manches longues', desc: 'Renforts coques épaules & coudes idéalement' },
      { label: 'Pantalon long solide', desc: 'Jean renforcé ou pantalon moto' },
      { label: 'Chaussures montantes', desc: 'Cheville protégée, semelle antidérapante' },
      { label: 'Gilet haute visibilité', desc: 'Obligatoire à bord (art. R431-1-2)' },
    ],
    section2Title: '🔧 Vérifications techniques rapides',
    items2: [
      { label: "Niveau d'huile", desc: 'À vérifier tous les 500 km' },
      { label: 'Liquide de frein', desc: 'Niveau + couleur (jaunâtre si vieilli)' },
      { label: 'Pneus (usure + pression)', desc: 'Sculpture > 1 mm · pression selon manuel' },
      { label: 'Feux + clignotants', desc: 'Croisement, route, stop, warnings, clignos' },
      { label: 'Chaîne de transmission', desc: 'Tension + graissage réguliers' },
    ],
  },
  {
    n: '03',
    title: 'Regard · Allure · Position',
    subtitle: 'Les 3 piliers de la conduite en circulation',
    intro: "REGARD : il conditionne TOUT le reste. Tu vas où tu regardes. ALLURE : jamais trop vite pour ce que tu vois, jamais trop lent pour la circulation autour. POSITION : défensive — assez à droite pour t'éloigner du frontal, assez au centre pour ne pas te faire doubler dans ta voie.",
    items: [
      { label: 'Regard porté LOIN', desc: '10-15 secondes en avant · pas ton pneu' },
      { label: 'Balayage régulier', desc: 'Panneaux, rétros tous les 5-8 sec' },
      { label: 'Allure adaptée aux limitations', desc: '50/80/90/110/130 selon le contexte' },
      { label: '1/3 droit hors agglomération', desc: 'Se ménage marge & visibilité' },
      { label: 'Milieu de voie en agglomération', desc: 'Empêche voitures de te doubler dans ta voie' },
      { label: 'Anticipation feux & carrefours', desc: "Décale ton allure avant d'y arriver" },
      { label: 'Mains détendues sur guidon', desc: 'Coudes libres, pas contractés' },
    ],
  },
  {
    n: '04',
    title: 'Trajectoire de sécurité en virage',
    subtitle: 'Méthode Élargir · Retarder · Freiner avant',
    intro: "En France, on roule sur la partie droite (art. R412-9). La trajectoire de sécurité ADAPTE cette règle au virage, elle ne la contredit JAMAIS. Virage à gauche : tu restes serré à DROITE (loin du frontal). Virage à droite : tu peux t'élargir modérément mais sans frôler l'axe.",
    items: [
      { label: 'Je freine AVANT le virage', desc: 'Tout le freinage en ligne droite' },
      { label: 'Je passe au bon rapport avant', desc: 'Vitesse et régime prêts pour la sortie' },
      { label: "Je m'élargis (avec discernement)", desc: 'Sans jamais frôler la ligne médiane' },
      { label: 'Point de corde RETARDÉ', desc: 'Je vois la sortie avant de pencher' },
      { label: 'Regard verrouillé sur la sortie', desc: 'La moto suit toujours le regard' },
      { label: 'Relance douce en sortie', desc: 'Progressive, moto redressée' },
      { label: '50 cm à 1 m de la ligne médiane', desc: 'JAMAIS proche du frontal' },
    ],
  },
  {
    n: '05',
    title: 'Cohabiter avec les autres usagers',
    subtitle: 'Croisements · Dépassements · Priorités',
    intro: "Les 2RM représentent ~22 % des tués pour seulement 2 % du trafic. La plupart des accidents en circulation impliquent un autre usager qui n'a pas vu la moto. Ton meilleur atout : anticiper les erreurs des autres avant qu'elles n'arrivent.",
    items: [
      { label: 'Croisement voiture', desc: '50 cm à 1 m de marge — jamais coller la ligne' },
      { label: "Dépassement d'un vélo/scooter", desc: 'Marge latérale > 1 m (art. R414-4)' },
      { label: 'Cohabitation camion / bus', desc: 'Angle mort massif — ne JAMAIS rester à leur droite' },
      { label: 'Angle mort auto', desc: 'Passe rapidement, ou reste bien derrière' },
      { label: 'Traversée piste cyclable', desc: 'Attends que le cycliste soit passé' },
      { label: 'Priorité à droite en ville', desc: 'Ralentis SYSTÉMATIQUEMENT à toute intersection non signalée' },
      { label: 'Passage piéton', desc: "Cède le passage AUSSI aux piétons qui s'apprêtent à traverser (loi 2018)" },
    ],
  },
  {
    n: '06',
    title: 'Vérifications sécurité (question orale)',
    subtitle: "La question orale avant l'épreuve en circulation",
    intro: "Avant de partir en circulation, l'inspecteur peut te poser une question sur la sécurité active/passive : freinage, éclairage, vent, pluie, chargement, transport passager, groupe... Notation : jusqu'à 3 points bonus/malus qui peuvent basculer une épreuve limite.",
    items: [
      { label: 'Frein AVANT : dosage progressif', desc: '70 % de la puissance de freinage · à privilégier ligne droite' },
      { label: 'Frein ARRIÈRE : stabilité', desc: "Aide à l'équilibre · complète le frein avant" },
      { label: "Rôle de l'ABS", desc: 'Empêche blocage roues (obligatoire depuis 2016 sur A2)' },
      { label: 'Éclairage : allumé en permanence', desc: 'Feux de croisement obligatoires jour et nuit (art. R416-1)' },
      { label: 'Vent de travers / rafales', desc: 'Souple sur guidon · ralentir en zone exposée' },
      { label: 'Pluie / route mouillée', desc: 'Distances × 2 · évite marquages au sol' },
      { label: 'Conduite de nuit', desc: 'Gilet rétro · vitesse réduite · distances triplées' },
    ],
  },
  {
    n: '07',
    title: "Prêt pour l'examen ?",
    subtitle: "Check-list finale avant l'épreuve",
    intro: "L'épreuve dure ~40 minutes sur route ouverte. Tu es équipé d'un casque radio pour recevoir les consignes de l'inspecteur qui suit en voiture derrière toi. Notation : A = Favorable · B = Insuffisant · C = Défavorable. Coefficient 3 sur le résultat final.",
    items: [
      { label: 'Plateau validé (6 manœuvres)', desc: 'Prérequis absolu' },
      { label: 'Code moto (ETM) obtenu', desc: 'Depuis moins de 5 ans' },
      { label: '20h de circulation minimum', desc: 'Volume moyen pour se sentir à l\'aise' },
      { label: "J'ai roulé en 4 milieux", desc: 'Ville, hors agglo, autoroute, nuit ou pluie' },
      { label: 'Question orale préparée', desc: 'Chapitre 6 révisé' },
      { label: 'Mon moniteur me valide GO !', desc: 'La certitude vaut mieux que le doute' },
      { label: 'EPI complet vérifié le jour J', desc: 'Casque, gants, blouson, gilet, papiers' },
    ],
  },
];

function CheckLine({ item }) {
  return (
    <div className="print-check-line">
      <Square className="print-check-icon" strokeWidth={1.5} />
      <div>
        <div className="print-check-label">{item.label}</div>
        {item.desc && <div className="print-check-desc">{item.desc}</div>}
      </div>
    </div>
  );
}

function ChapterPrint({ ch }) {
  return (
    <section className="print-chapter" data-testid={`print-chapter-${ch.n}`}>
      <div className="print-chapter-header">
        <div className="print-chapter-num">Chapitre {ch.n}</div>
        <h2 className="print-chapter-title">{ch.title}</h2>
        {ch.subtitle && <div className="print-chapter-subtitle">{ch.subtitle}</div>}
      </div>

      {ch.intro && (
        <div className="print-chapter-intro">
          <div className="print-chapter-intro-label">À COMPRENDRE</div>
          <p>{ch.intro}</p>
        </div>
      )}

      {ch.section1Title && <div className="print-section-title">{ch.section1Title}</div>}
      <div className="print-items">
        {ch.items.map((item, i) => (
          <CheckLine key={i} item={item} />
        ))}
      </div>

      {ch.section2Title && (
        <>
          <div className="print-section-title">{ch.section2Title}</div>
          <div className="print-items">
            {ch.items2.map((item, i) => (
              <CheckLine key={i} item={item} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default function LivretCirculationPrint() {
  useEffect(() => {
    document.title = 'Livret Circulation A2 · Version imprimable · CODE 2 ROUES';
    // Auto-lance l'impression après un court délai (laisse le temps au rendu)
    const t = setTimeout(() => {
      try { window.print(); } catch (_) { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="print-livret" data-testid="livret-circulation-print">
      {/* Styles imprimables inline (contenu-page uniquement) */}
      <style>{`
        @page {
          size: A4;
          margin: 18mm 15mm;
        }
        .print-livret {
          background: white;
          color: #111;
          font-family: 'Figtree', system-ui, -apple-system, sans-serif;
          max-width: 210mm;
          margin: 0 auto;
          padding: 24px;
          min-height: 100vh;
        }

        /* Sticky toolbar visible seulement à l'écran */
        .print-toolbar {
          position: sticky;
          top: 0;
          background: white;
          border-bottom: 2px solid #0891b2;
          padding: 12px 16px;
          display: flex;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
          z-index: 10;
          margin: -24px -24px 24px -24px;
        }
        .print-toolbar-actions { display: flex; gap: 10px; }
        .print-toolbar-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 13px;
          border: 2px solid #0891b2;
          color: #0891b2;
          background: white;
          cursor: pointer;
          text-decoration: none;
        }
        .print-toolbar-btn-primary {
          background: #0891b2;
          color: white;
        }

        .print-cover {
          text-align: center;
          padding: 40px 20px 60px;
          border-bottom: 4px solid #0891b2;
          margin-bottom: 32px;
        }
        .print-cover-kicker {
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 3px;
          color: #0891b2;
          margin-bottom: 12px;
          text-transform: uppercase;
        }
        .print-cover-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 42px;
          font-weight: 900;
          line-height: 1.05;
          margin: 0 0 10px;
        }
        .print-cover-title span { color: #0891b2; }
        .print-cover-baseline {
          font-size: 13px;
          font-weight: 900;
          letter-spacing: 3px;
          color: #555;
          margin-top: 14px;
        }
        .print-cover-info {
          margin-top: 20px;
          padding: 14px 20px;
          background: #f0f9ff;
          border: 1px solid #0891b2;
          border-radius: 12px;
          font-size: 12px;
          display: inline-block;
        }

        .print-profil {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
          margin: 24px 0 40px;
          padding: 20px;
          border: 2px dashed #999;
          border-radius: 12px;
        }
        .print-profil-field { }
        .print-profil-label {
          font-size: 9px;
          font-weight: 900;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #666;
          margin-bottom: 6px;
        }
        .print-profil-line {
          border-bottom: 1px solid #666;
          height: 26px;
        }

        .print-chapter {
          page-break-before: always;
          margin-top: 20px;
        }
        .print-chapter:first-of-type { page-break-before: auto; }
        .print-chapter-header {
          border-bottom: 3px solid #0891b2;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        .print-chapter-num {
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 3px;
          color: #0891b2;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .print-chapter-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 28px;
          font-weight: 900;
          line-height: 1.1;
          margin: 0 0 4px;
        }
        .print-chapter-subtitle {
          font-style: italic;
          color: #666;
          font-size: 13px;
        }

        .print-chapter-intro {
          background: #f8fafc;
          border-left: 4px solid #0891b2;
          padding: 12px 16px;
          margin: 16px 0 20px;
          border-radius: 0 8px 8px 0;
        }
        .print-chapter-intro-label {
          font-size: 9px;
          font-weight: 900;
          letter-spacing: 2px;
          color: #0891b2;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .print-chapter-intro p {
          margin: 0;
          font-size: 12px;
          line-height: 1.6;
        }

        .print-section-title {
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #333;
          margin: 20px 0 10px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 4px;
        }

        .print-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .print-check-line {
          display: flex;
          gap: 10px;
          padding: 8px 10px;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          break-inside: avoid;
        }
        .print-check-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          margin-top: 2px;
          color: #666;
        }
        .print-check-label {
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .print-check-desc {
          font-size: 10.5px;
          color: #666;
          line-height: 1.4;
        }

        .print-footer {
          page-break-before: always;
          text-align: center;
          padding-top: 60px;
          font-size: 11px;
          color: #666;
        }
        .print-footer-badge {
          display: inline-block;
          padding: 8px 20px;
          border: 2px solid #0891b2;
          border-radius: 20px;
          color: #0891b2;
          font-weight: 900;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-size: 11px;
          margin-bottom: 20px;
        }

        /* Mode impression : cacher la toolbar */
        @media print {
          .print-toolbar { display: none !important; }
          .print-livret {
            padding: 0;
            box-shadow: none;
          }
          body { background: white; }
        }
      `}</style>

      {/* Barre d'action (uniquement écran) */}
      <div className="print-toolbar">
        <Link to="/livret-circulation" className="print-toolbar-btn">
          <ArrowLeft size={14} /> Retour au livret
        </Link>
        <div className="print-toolbar-actions">
          <button
            onClick={() => window.print()}
            className="print-toolbar-btn print-toolbar-btn-primary"
            data-testid="btn-print"
          >
            <Printer size={14} /> Imprimer / Enregistrer en PDF
          </button>
        </div>
      </div>

      {/* Couverture */}
      <div className="print-cover">
        <div className="print-cover-kicker">CODE2ROUES.FR · VERSION IMPRIMABLE</div>
        <h1 className="print-cover-title">
          Livret élève<br />
          <span>Circulation A1/A2</span>
        </h1>
        <div className="print-cover-baseline">SORTIR · OBSERVER · ANTICIPER</div>
        <div className="print-cover-info">
          <strong>7 chapitres pédagogiques</strong> · Épreuve circulation A2 (~40 min sur route, coefficient 3)
        </div>
      </div>

      {/* Profil élève */}
      <div className="print-profil">
        <div className="print-profil-field">
          <div className="print-profil-label">Élève</div>
          <div className="print-profil-line" />
        </div>
        <div className="print-profil-field">
          <div className="print-profil-label">Moniteur / auto-école</div>
          <div className="print-profil-line" />
        </div>
        <div className="print-profil-field">
          <div className="print-profil-label">Début formation</div>
          <div className="print-profil-line" />
        </div>
      </div>

      {/* Sommaire */}
      <div style={{ marginBottom: 40 }}>
        <div className="print-section-title">Sommaire</div>
        <ol style={{ paddingLeft: 20, fontSize: 12, lineHeight: 1.8 }}>
          {CHAPTERS_DATA.map((ch) => (
            <li key={ch.n}>
              <strong>Chapitre {ch.n}</strong> — {ch.title}
            </li>
          ))}
        </ol>
      </div>

      {/* Les 7 chapitres */}
      {CHAPTERS_DATA.map((ch) => (
        <ChapterPrint key={ch.n} ch={ch} />
      ))}

      {/* Footer final */}
      <div className="print-footer">
        <div className="print-footer-badge">Tu es prêt(e)</div>
        <div>Fais confiance à ton moniteur, à ton apprentissage, et à tes automatismes.</div>
        <div style={{ marginTop: 12, fontSize: 10, color: '#999' }}>
          © Bruno LLORENS · ECSR diplômé d'État · CODE 2 ROUES · 2026<br />
          Support pédagogique complémentaire au livret plateau · code2roues.fr
        </div>
      </div>
    </div>
  );
}
